import { Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import { addDays, format, parseISO } from 'date-fns';
import { JobWithRelations } from '@/types';
import { CustomerLink } from '@/components/customers/CustomerLink';
import { useCustomerIdsByPhone } from '@/hooks/useCustomers';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, ExternalLink } from 'lucide-react';
import { useCompanyLocale } from '@/hooks/useCompanyLocale';
import { TechProgressBadge } from '@/components/jobs/TechProgressBadge';
import { formatInTz } from '@/utils/timezone';

interface JobsTableViewProps {
  jobs: JobWithRelations[];
  showMoreButton?: React.ReactNode;
  onJobClick?: (jobId: string) => void;
  showCancelReason?: boolean;
  /** Upcoming: a header row before each appointment day (rows must already be sorted). */
  groupByDay?: { timezone: string; today: string };
}

function dayKeyOf(job: JobWithRelations, timezone: string): string {
  return job.scheduled_date ? formatInTz(job.scheduled_date, timezone, 'yyyy-MM-dd') : '';
}

function dayHeading(key: string, today: string): string {
  if (!key) return 'No date set';
  if (key === today) return 'Today';
  if (key === format(addDays(parseISO(today), 1), 'yyyy-MM-dd')) return 'Tomorrow';
  const label = format(parseISO(key), 'EEEE d MMM');
  return key < today ? `Overdue · ${label}` : label;
}

const PAYMENT_CONFIG: Record<string, { label: string; variant: 'success' | 'warning' | 'destructive-soft' }> = {
  paid: { label: 'Paid', variant: 'success' },
  partially_paid: { label: 'Partial', variant: 'warning' },
  unpaid: { label: 'Unpaid', variant: 'destructive-soft' },
};

function getInitials(name: string): string {
  return name
    .trim()
    .split(/\s+/)
    .map((part) => part[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

export function JobsTableView({ jobs, showMoreButton, onJobClick, showCancelReason = false, groupByDay }: JobsTableViewProps) {
  const navigate = useNavigate();
  const { formatShortDate, formatMoney } = useCompanyLocale();
  // Jobs with no customer_id still link through when their phone matches a customer.
  const resolveCustomerId = useCustomerIdsByPhone(jobs);
  const today = groupByDay?.today ?? '';

  const handleRowClick = (job: JobWithRelations) => {
    if (onJobClick) {
      onJobClick(job.id);
    } else {
      navigate(`/jobs/${job.id}`);
    }
  };

  return (
    <>
      <Table>
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <TableHead>Job #</TableHead>
            <TableHead>Customer</TableHead>
            <TableHead>Phone</TableHead>
            <TableHead>Service Type</TableHead>
            <TableHead>Technician</TableHead>
            <TableHead>Payment</TableHead>
            <TableHead className="text-right">Revenue</TableHead>
            <TableHead>Scheduled</TableHead>
            <TableHead>Address</TableHead>
            {showCancelReason && <TableHead>Reason</TableHead>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {jobs.map((job, index) => {
            const payment = PAYMENT_CONFIG[job.payment_status];
            const dayKey = groupByDay ? dayKeyOf(job, groupByDay.timezone) : null;
            const newDay = groupByDay && (index === 0 || dayKeyOf(jobs[index - 1], groupByDay.timezone) !== dayKey);
            return (
              <Fragment key={job.id}>
              {newDay && (
                <TableRow className="bg-muted/40 hover:bg-muted/40">
                  <TableCell
                    colSpan={showCancelReason ? 10 : 9}
                    className={`py-1.5 text-[12px] font-semibold ${dayKey && dayKey < today ? 'text-destructive' : 'text-muted-foreground'}`}
                  >
                    {dayHeading(dayKey ?? '', today)}
                  </TableCell>
                </TableRow>
              )}
              <TableRow
                className="cursor-pointer hover:bg-muted/50"
                onClick={() => handleRowClick(job)}
              >
                <TableCell className="font-medium tabular-nums text-muted-foreground">{job.job_number}</TableCell>
                <TableCell>
                  <CustomerLink
                    customerId={resolveCustomerId(job)}
                    name={job.customer_name}
                    className="font-medium"
                    showUnlinkedWarning
                  />
                </TableCell>
                <TableCell className="tabular-nums">{job.customer_phone}</TableCell>
                <TableCell>
                  {job.service_types?.name ? (
                    job.service_types.name
                  ) : (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="inline-flex items-center gap-1.5 text-warning">
                            <AlertTriangle className="h-3.5 w-3.5" />
                            Not set
                          </span>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Missing service type - click to edit job</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                </TableCell>
                <TableCell>
                  {job.techs?.name ? (
                    <div className="flex flex-col gap-1">
                      <div className="flex items-center gap-2">
                        <span className="flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-md bg-sidebar text-[10px] font-semibold text-sidebar-foreground">
                          {getInitials(job.techs.name)}
                        </span>
                        <span>{job.techs.name}</span>
                      </div>
                      <TechProgressBadge job={job} className="w-fit text-[10px] px-1.5 py-0" />
                    </div>
                  ) : (
                    <span className="text-muted-foreground">-</span>
                  )}
                </TableCell>
                <TableCell>
                  {payment ? (
                    <Badge variant={payment.variant}>{payment.label}</Badge>
                  ) : (
                    <Badge variant="secondary">{job.payment_status.replace('_', ' ')}</Badge>
                  )}
                </TableCell>
                <TableCell className="text-right font-semibold tabular-nums">
                  {job.invoice_total ? formatMoney(job.invoice_total) : '-'}
                </TableCell>
                <TableCell>
                  {job.scheduled_date ? (
                    <div className="flex flex-col gap-0.5">
                      <span>{formatShortDate(job.scheduled_date)}</span>
                      {(job.scheduled_time_start || job.scheduled_time_end) && (
                        <span className="text-[11px] text-muted-foreground">
                          {[job.scheduled_time_start, job.scheduled_time_end]
                            .filter(Boolean)
                            .map((t) => {
                              const [h, m] = (t as string).split(':').map(Number);
                              const period = h >= 12 ? 'PM' : 'AM';
                              const hour = h % 12 || 12;
                              return m === 0 ? `${hour}${period}` : `${hour}:${String(m).padStart(2, '0')}${period}`;
                            })
                            .join(' – ')}
                        </span>
                      )}
                    </div>
                  ) : '-'}
                </TableCell>
                <TableCell className="max-w-[200px] truncate">
                  {job.customer_address}
                </TableCell>
                {showCancelReason && (
                  <TableCell className="max-w-[240px]">
                    {job.cancellation_reason ? (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="block truncate text-destructive">
                              {job.cancellation_reason}
                            </span>
                          </TooltipTrigger>
                          <TooltipContent className="max-w-xs">
                            <p className="whitespace-pre-wrap">{job.cancellation_reason}</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                )}
              </TableRow>
              </Fragment>
            );
          })}
        </TableBody>
      </Table>
      {showMoreButton}
    </>
  );
}
